import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Select, MenuItem,
  FormControl, InputLabel, SelectChangeEvent, Button, TextField,
  Menu, Tooltip,
} from '@mui/material';
import {
  InfoOutlined, TrendingUpOutlined, TokenOutlined, AppsOutlined,
  AttachMoneyOutlined, TableChartOutlined, DownloadOutlined,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import apiService from '../services/api';
import EmptyState from '../components/EmptyState';

type Period = 'day' | 'week' | 'month';

interface SummaryData {
  total_input_tokens: number;
  total_output_tokens: number;
  estimated_total_cost: number;
  active_apps: number;
  days_tracked?: number;
  total_requests?: number;
  cost_disclaimer?: string;
}

interface PeriodPoint {
  label: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost: number;
  request_count?: number;
}

interface ModelRow {
  model: string;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  cache_write_tokens: number;
  request_count: number;
  estimated_cost: number;
  cache_savings: number;
  cache_write_cost: number;
  short_context_cost: number;
  long_context_cost: number;
}

interface AppOption {
  app_id: string;
  app_name: string;
}

const fmtTokens = (n: number | undefined | null): string => {
  const v = Number(n ?? 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

const fmtCost = (n: number | undefined | null): string =>
  `$${(Number(n ?? 0)).toFixed(4)}`;

const fmtCostBig = (n: number | undefined | null): string => {
  const v = Number(n ?? 0);
  return v >= 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(4)}`;
};

const exportToJSON = (data: any, filename: string) => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  window.URL.revokeObjectURL(url);
};

const AnalyticsPage: React.FC = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const { appId: urlAppId } = useParams<{ appId: string }>();
  const urlInitializedRef = useRef(false);
  const isDark = mode === 'dark';

  const card     = isDark ? '#111111' : '#FFFFFF';
  const border   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP    = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS    = isDark ? '#888888' : '#666666';
  const textM    = isDark ? '#555555' : '#AAAAAA';
  const subBg    = isDark ? '#0D0D0D' : '#F9F9F9';
  const rowHover = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';

  const [period, setPeriod]       = useState<Period>('day');
  const [appId, setAppId]         = useState<string>(urlAppId ?? '');
  const [apps, setApps]           = useState<AppOption[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate]     = useState<string>('');
  const [useCustomDates, setUseCustomDates] = useState<boolean>(false);

  const [activeApps, setActiveApps]   = useState<number>(0);
  const [periodData, setPeriodData]   = useState<PeriodPoint[]>([]);
  const [lifetimeData, setLifetimeData] = useState<SummaryData | null>(null);
  const [modelData, setModelData]     = useState<ModelRow[]>([]);

  const [loadingPeriod, setLoadingPeriod]     = useState(true);
  const [loadingModels, setLoadingModels]     = useState(true);
  const [loadingLifetime, setLoadingLifetime] = useState(true);
  const [exportAnchor, setExportAnchor]       = useState<null | HTMLElement>(null);

  // Fetch global active app count once — summary endpoint has no app/period filter
  useEffect(() => {
    apiService.getAnalyticsSummary()
      .then((s: SummaryData) => setActiveApps(s.active_apps ?? 0))
      .catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    setLoadingPeriod(true);
    setLoadingModels(true);
    setLoadingLifetime(true);

    const appFilter = appId || undefined;
    const start = useCustomDates ? startDate : undefined;
    const end = useCustomDates ? endDate : undefined;

    try {
      const p = await apiService.getAnalyticsByPeriod(period, appFilter);
      setPeriodData(Array.isArray(p.data) ? p.data : []);
    } catch { } finally { setLoadingPeriod(false); }

    try {
      const m = await apiService.getAnalyticsByModel(start, end, appFilter);
      const modelsDict: Record<string, any> = m.models ?? {};
      const rows: ModelRow[] = Array.isArray(modelsDict)
        ? modelsDict
        : Object.entries(modelsDict).map(([model, entry]: [string, any]) => ({
            model,
            input_tokens: entry.input_tokens ?? 0,
            output_tokens: entry.output_tokens ?? 0,
            cached_tokens: entry.cached_tokens ?? 0,
            cache_write_tokens: entry.cache_write_tokens ?? 0,
            request_count: entry.request_count ?? 0,
            estimated_cost: entry.cost?.total_cost ?? entry.estimated_cost ?? 0,
            cache_savings: entry.cost?.cache_savings ?? 0,
            cache_write_cost: entry.cost?.cache_write_cost ?? 0,
            short_context_cost: entry.cost?.short_context_cost ?? 0,
            long_context_cost: entry.cost?.long_context_cost ?? 0,
          }));
      rows.sort((a, b) => b.estimated_cost - a.estimated_cost);
      setModelData(rows);
    } catch { } finally { setLoadingModels(false); }

    try {
      const lifetime = await apiService.getAnalyticsSummary(start, end);
      setLifetimeData(lifetime);
    } catch { } finally { setLoadingLifetime(false); }
  }, [period, appId, useCustomDates, startDate, endDate]);

  useEffect(() => {
    apiService.listApps().then((list: AppOption[]) => {
      setApps(list);
      // Seed appId from URL param once apps are loaded
      if (urlAppId && !urlInitializedRef.current) {
        urlInitializedRef.current = true;
        const match = list.find((a: AppOption) => a.app_id === urlAppId);
        if (match) setAppId(urlAppId);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAppFilterChange = (val: string) => {
    setAppId(val);
    navigate(val ? `/analytics/${val}` : '/analytics', { replace: true });
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  // Show latest period metrics (today/this week/this month) at top
  const latestPeriod = periodData[periodData.length - 1];
  const totalInput  = latestPeriod?.input_tokens ?? 0;
  const totalOutput = latestPeriod?.output_tokens ?? 0;
  const totalCost   = latestPeriod?.estimated_cost ?? 0;

  const METRICS = [
    { label: 'Total Cost',    value: fmtCostBig(totalCost),                       icon: <AttachMoneyOutlined sx={{ fontSize: 16 }} />, accent: '#22C55E' },
    { label: 'Input Tokens',  value: fmtTokens(totalInput),                       icon: <TokenOutlined sx={{ fontSize: 16 }} />,       accent: '#3B82F6' },
    { label: 'Output Tokens', value: fmtTokens(totalOutput),                      icon: <TrendingUpOutlined sx={{ fontSize: 16 }} />,  accent: '#8B5CF6' },
    { label: 'Active Apps',   value: String(appId ? 1 : activeApps),              icon: <AppsOutlined sx={{ fontSize: 16 }} />,        accent: '#F59E0B' },
  ];

  const PERIOD_LABELS: Record<Period, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Lifetime Metrics Section */}
      {loadingLifetime ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 3 }}>
          <CircularProgress size={20} sx={{ color: textM }} />
        </Box>
      ) : lifetimeData ? (
        <Box sx={{
          mb: 4, px: 3, py: 2.5,
          bgcolor: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
          border: '1px solid rgba(34,197,94,0.2)', borderRadius: '8px',
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#22C55E', letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5 }}>
            📊 Lifetime Metrics (All Time)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mb: 0.5 }}>Total Cost</Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#22C55E' }}>
                {fmtCostBig(lifetimeData.estimated_total_cost)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mb: 0.5 }}>Input Tokens</Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP }}>
                {fmtTokens(lifetimeData.total_input_tokens)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mb: 0.5 }}>Output Tokens</Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP }}>
                {fmtTokens(lifetimeData.total_output_tokens)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mb: 0.5 }}>Days Tracked</Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP }}>
                {lifetimeData.days_tracked}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP, letterSpacing: '-0.03em', lineHeight: 1.2, mb: 0.5 }}>
            Analytics
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: textS }}>
            Platform-wide token usage and cost estimates
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>App filter</InputLabel>
            <Select
              value={appId}
              label="App filter"
              onChange={(e: SelectChangeEvent) => handleAppFilterChange(e.target.value)}
              sx={{ fontSize: '0.82rem', bgcolor: card }}
            >
              <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All apps</MenuItem>
              {apps.map(a => (
                <MenuItem key={a.app_id} value={a.app_id} sx={{ fontSize: '0.82rem' }}>
                  {a.app_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Period pills */}
          <Tooltip title="Day / week / month buckets are aggregated in UTC on the server, not per-viewer. Raw timestamps elsewhere in the dashboard use your timezone." arrow>
            <Typography sx={{ fontSize: '0.7rem', color: textM, mr: 0.5, cursor: 'help', fontWeight: 600, letterSpacing: '0.05em' }}>
              UTC
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden' }}>
            {(['day', 'week', 'month'] as Period[]).map(p => (
              <Box
                key={p}
                onClick={() => { setPeriod(p); setUseCustomDates(false); }}
                sx={{
                  px: 2, py: 0.75, cursor: 'pointer',
                  bgcolor: (period === p && !useCustomDates) ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : 'transparent',
                  borderRight: p !== 'month' ? `1px solid ${border}` : 'none',
                  transition: 'background 0.1s',
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                <Typography sx={{
                  fontSize: '0.78rem', fontWeight: (period === p && !useCustomDates) ? 600 : 400,
                  color: (period === p && !useCustomDates) ? textP : textS,
                  letterSpacing: '-0.01em',
                }}>
                  {PERIOD_LABELS[p]}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Export button with menu */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadOutlined sx={{ fontSize: 14 }} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={{
              fontSize: '0.78rem', textTransform: 'none',
              borderColor: border, color: textP,
              '&:hover': { borderColor: textP, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                if (periodData.length > 0) {
                  const filename = `analytics-${period}-${new Date().toISOString().split('T')[0]}`;
                  const headers = ['Period', 'Input Tokens', 'Output Tokens', 'Estimated Cost', 'Request Count'];
                  const csv = [
                    headers.join(','),
                    ...periodData.map((d: any) =>
                      `"${d.input_tokens}",${d.input_tokens},${d.output_tokens},${d.estimated_cost},${d.request_count || 0}`
                    )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${filename}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }
                setExportAnchor(null);
              }}
              sx={{ fontSize: '0.8rem' }}
            >
              📊 Export as CSV
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (periodData.length > 0) {
                  exportToJSON({
                    period,
                    app_id: appId || 'all',
                    export_date: new Date().toISOString(),
                    data: periodData,
                  }, `analytics-${period}-${new Date().toISOString().split('T')[0]}`);
                }
                setExportAnchor(null);
              }}
              sx={{ fontSize: '0.8rem' }}
            >
              📄 Export as JSON
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Custom date range */}
      <Box sx={{ mb: 3, display: useCustomDates ? 'flex' : 'none', gap: 1.5, alignItems: 'center' }}>
        <TextField
          type="date"
          size="small"
          label="Start Date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 150, '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
        />
        <TextField
          type="date"
          size="small"
          label="End Date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 150, '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
        />
        <Button
          size="small"
          onClick={() => { setUseCustomDates(false); setStartDate(''); setEndDate(''); }}
          sx={{ fontSize: '0.78rem', color: textS }}
        >
          Clear
        </Button>
      </Box>

      {/* Toggle custom dates */}
      {!useCustomDates && (
        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setUseCustomDates(true)}
            sx={{ fontSize: '0.78rem', color: '#8B5CF6', textTransform: 'none' }}
          >
            📅 Use custom date range
          </Button>
        </Box>
      )}

      {/* Cost disclaimer */}
      <Box sx={{
        mb: 3, px: 3, py: 1.5,
        bgcolor: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px',
        display: 'flex', gap: 1.5, alignItems: 'center',
      }}>
        <InfoOutlined sx={{ fontSize: 14, color: '#F59E0B', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: '#F59E0B' }}>
          Cost estimates use cached pricing data and may not reflect current OpenAI rates.
          Always verify at <strong>openai.com/pricing</strong>.
        </Typography>
      </Box>

      {/* Summary cards */}
      {loadingPeriod ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} sx={{ color: textM }} />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 4 }}>
          {METRICS.map(m => (
            <Box key={m.label} sx={{ bgcolor: card, border: `1px solid ${border}`, borderRadius: '10px', p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {m.label}
                </Typography>
                <Box sx={{ color: m.accent, opacity: 0.8 }}>{m.icon}</Box>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: textP, letterSpacing: '-0.04em', lineHeight: 1 }}>
                {m.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* By model table */}
      <Box sx={{ bgcolor: card, border: `1px solid ${border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: textP, letterSpacing: '-0.02em' }}>
            Usage by Model
          </Typography>
        </Box>

        {/* Table header */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 150px 110px 150px 110px 140px',
          px: 3, py: 1.5, bgcolor: subBg, borderBottom: `1px solid ${border}`,
        }}>
          {['Model', 'Input tokens', 'Cached', 'Output tokens', 'Requests', 'Est. cost'].map((col, i) => (
            <Typography key={i} sx={{ fontSize: '0.68rem', fontWeight: 600, color: textM, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {col}
            </Typography>
          ))}
        </Box>

        {loadingModels ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} sx={{ color: textM }} />
          </Box>
        ) : modelData.length === 0 ? (
          <EmptyState
            icon={<TableChartOutlined sx={{ fontSize: 20 }} />}
            code="NO_DATA · EMPTY_RESULT"
            title="No model breakdown yet"
            subtitle="Per-model usage will populate as requests flow through the proxy."
            compact
          />
        ) : (
          modelData.map((row, idx) => (
            <Box
              key={row.model}
              sx={{
                display: 'grid', gridTemplateColumns: '1fr 150px 110px 150px 110px 140px',
                px: 3, py: 1.75, alignItems: 'center',
                borderBottom: idx < modelData.length - 1 ? `1px solid ${border}` : 'none',
                '&:hover': { bgcolor: rowHover },
                transition: 'background 0.1s',
              }}
            >
              <Typography sx={{
                fontFamily: '"JetBrains Mono","Courier New",monospace',
                fontSize: '0.78rem', fontWeight: 500, color: textP,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 2,
              }}>
                {row.model}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: textP }}>
                  {fmtTokens(row.input_tokens)}
                </Typography>
                <Box sx={{ flex: 1, height: 3, bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', borderRadius: 2, maxWidth: 60 }}>
                  <Box sx={{
                    height: '100%', borderRadius: 2, bgcolor: '#3B82F6',
                    width: `${Math.min(100, (row.input_tokens / (modelData[0]?.input_tokens || 1)) * 100)}%`,
                  }} />
                </Box>
              </Box>
              <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: row.cached_tokens > 0 ? '#22C55E' : textM }}>
                {row.cached_tokens > 0 ? fmtTokens(row.cached_tokens) : '—'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: textP }}>
                  {fmtTokens(row.output_tokens)}
                </Typography>
                <Box sx={{ flex: 1, height: 3, bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', borderRadius: 2, maxWidth: 60 }}>
                  <Box sx={{
                    height: '100%', borderRadius: 2, bgcolor: '#8B5CF6',
                    width: `${Math.min(100, (row.output_tokens / (modelData[0]?.output_tokens || 1)) * 100)}%`,
                  }} />
                </Box>
              </Box>
              <Typography sx={{ fontSize: '0.82rem', color: textS }}>
                {row.request_count.toLocaleString()}
              </Typography>
              <Box>
                <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: '#22C55E' }}>
                  {fmtCost(row.estimated_cost)}
                </Typography>
                {row.short_context_cost > 0 && row.long_context_cost > 0 ? (
                  <Typography sx={{ fontSize: '0.62rem', color: textM }}>
                    {fmtCost(row.short_context_cost)} std + {fmtCost(row.long_context_cost)} long-ctx
                  </Typography>
                ) : row.long_context_cost > 0 ? (
                  <Typography sx={{ fontSize: '0.62rem', color: '#F59E0B' }}>long-context rate</Typography>
                ) : null}
                {row.cache_write_cost > 0 && (
                  <Typography sx={{ fontSize: '0.62rem', color: '#F59E0B' }}>
                    +{fmtCost(row.cache_write_cost)} cache write
                  </Typography>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsPage;
